import {isNavigatorOffline} from 'src/offline/connectivity';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import type {AxiosResponse} from 'axios';
import axios from 'axios';
import {checkApiVersion} from 'src/app/routing/versionService';

export const handleResponseSuccess = (
  response: AxiosResponse
): AxiosResponse => {
  // Check API version header on every successful response
  if (response.headers) {
    checkApiVersion(response.headers as Record<string, string>);
  }
  return response;
};

// Session-expiry 401 handling shared by the axios interceptor and the SSE
// stream path, which bypasses axios.
export const handleUnauthorized = (): void => {
  if (isNavigatorOffline()) {
    return;
  }

  if (window.location.pathname !== PathUtility.Logout()) {
    console.log('Invalid session. Redircting user to logout page.');
    window.location.assign(PathUtility.Logout());
  } else {
    console.log('User already logged out. Redirecting home.');
  }
};

export const handleResponseError = (error: unknown): Promise<never> => {
  console.log(error);
  const typedError = error as {response?: {status?: number}; status?: number};
  const status = typedError?.response?.status ?? typedError?.status;

  if (401 === status) {
    handleUnauthorized();
  }

  return Promise.reject(error);
};

const blobToText = (blob: Blob): Promise<string> =>
  typeof blob.text === 'function'
    ? blob.text()
    : new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result ?? ''));
        reader.onerror = () => reject(reader.error);
        reader.readAsText(blob);
      });

// File endpoints use `responseType: 'blob'`, so an error body arrives as a Blob the sync
// message readers cannot parse; decode it to text before the generated client wraps it.
export const decodeBlobErrorBody = async (error: unknown): Promise<never> => {
  const response = (error as {response?: {data?: unknown}} | null)?.response;
  if (
    response &&
    typeof Blob !== 'undefined' &&
    response.data instanceof Blob
  ) {
    try {
      response.data = await blobToText(response.data);
    } catch {
      // Unreadable body: keep the Blob; callers fall back to the generic message.
    }
  }
  return Promise.reject(error);
};

axios.interceptors.response.use(handleResponseSuccess, handleResponseError);
